import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  DataIcon,
  IconProps,
} from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { Button } from "@/kit/ui/button";
import { Skeleton } from "@/kit/ui/skeleton";
import { ComponentType, MouseEvent, ReactNode } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { Link as BaseLink, generatePath } from "react-router";
import { DatasetVersionCardFragment$key } from "./__generated__/DatasetVersionCardFragment.graphql";
import DatasetVersionPublishButton from "./DatasetVersionPublishButton";
import EntityProfilePic from "./EntityProfilePic";
import { FormattedDataSize } from "./FormattedDataSize";
import { RelativeTime } from "./RelativeTime";
import { useVersion } from "@/common/VersionContext";

const DatasetVersionCardFragment = graphql`
  fragment DatasetVersionCardFragment on DatasetVersion {
    id
    version
    size
    publishedAt
    createdAt
    updatedAt
    dataset {
      id
      slug
      owner {
        username
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
    }

    ...DatasetVersionPublishButtonFragment
  }
`;

type Props = {
  datasetVersion: DatasetVersionCardFragment$key;
};

export function DatasetVersionCard({ datasetVersion }: Props) {
  const data = useFragment(DatasetVersionCardFragment, datasetVersion);
  const { openPanel } = useVersion();

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    openPanel.dispatch(true);
  };

  return (
    <div className="flex flex-col items-start justify-between bg-grey-25 border border-grey-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex flex-row items-center justify-between pl-3 pr-2 py-2 bg-linear-to-b from-white to-[#F9F8FC] border-b border-grey-200 group hover:cursor-pointer transition-colors hover:to-[#F8F7FF] focus:outline-solid focus:outline-1 focus:outline-offset-1 focus:outline-brand-100"
        onClick={onClick}
        type="button"
      >
        <p className="text-base font-semibold text-grey-900 leading-6 transition-colors group-hover:text-brand-500">
          <FormattedMessage
            defaultMessage="Version {version}"
            values={{ version: data.version }}
          />
        </p>
        <ChevronRightIcon className="w-5 h-5 m-2 text-grey-700 transition-colors group-hover:text-brand-500" />
      </button>
      <div className="w-full flex flex-col items-start px-3 pt-4 pb-3 gap-y-3">
        <MetadataItem
          title={<FormattedMessage defaultMessage="Created by:" />}
          value={
            <BaseLink
              to={generatePath("/:username", {
                username: data.dataset.owner.username,
              })}
            >
              <Badge hasPrefix shape="pill" className="gap-1">
                <EntityProfilePic entity={data.dataset.owner} size="4" />
                {data.dataset.owner.username}
              </Badge>
            </BaseLink>
          }
          text={false}
        />
        <MetadataItem
          title={<FormattedMessage defaultMessage="Created on: " />}
          icon={CalendarIcon}
          value={
            <FormattedDate
              value={data.createdAt}
              month="short"
              day="numeric"
              year="numeric"
            />
          }
        />
        <MetadataItem
          title={<FormattedMessage defaultMessage="Last edited: " />}
          icon={ClockIcon}
          value={<RelativeTime date={new Date(data.updatedAt)} />}
        />
        <MetadataItem
          title={<FormattedMessage defaultMessage="Size: " />}
          icon={DataIcon}
          value={<FormattedDataSize bytes={data.size} />}
        />
        {data.publishedAt ? (
          <Button
            className="w-full disabled:border-success-300 disabled:text-success-700 disabled:bg-success-50"
            variant="secondary"
            hue="success"
            disabled
          >
            <CheckCircleIcon className="w-5 h-5" />
            <FormattedMessage defaultMessage="Active" />
          </Button>
        ) : (
          <DatasetVersionPublishButton datasetVersion={data} className="w-full">
            <FormattedMessage defaultMessage="Publish version" />
          </DatasetVersionPublishButton>
        )}
      </div>
    </div>
  );
}

export type IconType = ComponentType<IconProps>;

export interface MetadataItemProps {
  title: ReactNode;
  value: ReactNode;
  icon?: IconType;
  text?: boolean;
}

export function MetadataItem({
  title,
  value,
  icon: Icon,
  text = true,
}: Readonly<MetadataItemProps>) {
  return (
    <div className="w-full flex flex-row items-center justify-between">
      <p className="text-grey-700 text-sm font-normal leading-5 line-clamp-1">
        {title}
      </p>
      <div className="flex flex-row items-center gap-2">
        {Icon && <Icon className="w-3 h-3 text-grey-600" />}
        {text ? (
          <p className="text-grey-900 text-sm font-medium leading-5 line-clamp-1">
            {value}
          </p>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

export function DatasetVersionCardSkeleton() {
  return (
    <div className="animate-pulse flex flex-col items-start justify-between bg-grey-25 border border-grey-200 rounded-xl overflow-hidden">
      <div className="w-full flex flex-row items-center justify-between border-b border-grey-200 bg-linear-to-b from-white to-[#F9F8FC] py-2 pr-2 pl-3">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-5 w-5 rounded">
          <span />
        </Skeleton>
      </div>

      <div className="w-full flex flex-col items-start px-3 pt-4 pb-3 gap-y-3">
        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-grey-600 text-xs font-normal">
            <FormattedMessage defaultMessage="Created by:" />
          </p>
          <div className="flex flex-row items-center gap-2">
            <Skeleton className="h-4 w-4 rounded-full">
              <span />
            </Skeleton>
            <Skeleton className="h-6 rounded-full px-3 inline-flex items-center">
              <span className="inline-block h-3 w-16" />
            </Skeleton>
          </div>
        </div>

        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-grey-600 text-xs font-normal">
            <FormattedMessage defaultMessage="Created on:" />
          </p>
          <div className="flex flex-row items-center gap-2">
            <Skeleton className="w-3 h-3 rounded">
              <span />
            </Skeleton>
            <Skeleton className="h-3 w-24 rounded">
              <span />
            </Skeleton>
          </div>
        </div>

        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-grey-600 text-xs font-normal">
            <FormattedMessage defaultMessage="Last edited:" />
          </p>
          <div className="flex flex-row items-center gap-2">
            <Skeleton className="w-3 h-3 rounded">
              <span />
            </Skeleton>
            <Skeleton className="h-3 w-20 rounded">
              <span />
            </Skeleton>
          </div>
        </div>

        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-grey-600 text-xs font-normal">
            <FormattedMessage defaultMessage="Size:" />
          </p>
          <div className="flex flex-row items-center gap-2">
            <Skeleton className="w-3 h-3 rounded">
              <span />
            </Skeleton>
            <Skeleton className="h-3 w-16 rounded">
              <span />
            </Skeleton>
          </div>
        </div>

        <Skeleton className="w-full h-9 rounded-lg">
          <span />
        </Skeleton>
      </div>
    </div>
  );
}
